/**
 * session-recorder/TesterSessionsPanel.tsx
 * 
 * Painel de Listagem, Filtro e Auditoria de Sessões de Testadores.
 * Agrupa sessões por versão da build, exibe localização geográfica e permite assistir ao Replay.
 */

import React, { useState, useEffect } from 'react';
import { 
  Play, 
  Download, 
  Trash2, 
  MapPin, 
  ShieldCheck, 
  AlertTriangle, 
  Clock, 
  Search, 
  ToggleLeft, 
  ToggleRight, 
  Activity, 
  Layers, 
  ExternalLink,
  Monitor,
  Smartphone,
  CheckCircle2,
  RefreshCw
} from 'lucide-react';
import type { RecordedSession } from './types';
import { 
  loadAllRecordedSessions, 
  deleteRecordedSession, 
  clearAllRecordedSessions, 
  exportSessionAsJson,
  isSessionRecorderEnabled,
  setSessionRecorderEnabled 
} from './sessionStorageService';
import { SessionReplayerModal } from './SessionReplayerModal';
import { toast } from 'sonner';

interface TesterSessionsPanelProps {
  currentVersion: string;
  onSelectSessionForReplay?: (session: RecordedSession) => void;
}

export const TesterSessionsPanel: React.FC<TesterSessionsPanelProps> = ({ currentVersion }) => {
  const [sessions, setSessions] = useState<RecordedSession[]>([]);
  const [filterText, setFilterText] = useState<string>('');
  const [versionFilter, setVersionFilter] = useState<'all' | 'current' | 'outdated'>('all');
  const [recorderEnabled, setRecorderEnabled] = useState<boolean>(isSessionRecorderEnabled());
  const [selectedSessionForReplay, setSelectedSessionForReplay] = useState<RecordedSession | null>(null);

  const reloadSessions = () => {
    const list = loadAllRecordedSessions();
    setSessions(list);
  };

  useEffect(() => {
    reloadSessions();
  }, []);

  const handleToggleRecorder = () => {
    const next = !recorderEnabled;
    setRecorderEnabled(next);
    setSessionRecorderEnabled(next);
    toast.success(next ? 'Gravação de sessões ATIVADA' : 'Gravação de sessões DESATIVADA');
  };

  const handleDeleteSession = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    deleteRecordedSession(id);
    reloadSessions();
    toast.info('Sessão removida');
  };

  const handleClearAll = () => {
    if (window.confirm('Deseja limpar todo o histórico de sessões gravadas localmente?')) {
      clearAllRecordedSessions();
      reloadSessions();
      toast.success('Histórico de sessões limpo com sucesso');
    }
  };

  // Filtragem
  const filteredSessions = sessions.filter((s) => {
    const matchText = 
      s.user.nome.toLowerCase().includes(filterText.toLowerCase()) ||
      s.user.email.toLowerCase().includes(filterText.toLowerCase()) ||
      s.appVersion.version.toLowerCase().includes(filterText.toLowerCase()) ||
      (s.location?.city && s.location.city.toLowerCase().includes(filterText.toLowerCase())) ||
      s.visitedRoutes.some((r) => r.toLowerCase().includes(filterText.toLowerCase()));

    if (!matchText) return false;

    if (versionFilter === 'current') {
      return s.appVersion.version === currentVersion;
    }
    if (versionFilter === 'outdated') {
      return s.appVersion.version !== currentVersion;
    }
    return true;
  });

  // Agrupamento por versão
  const versionGroups = filteredSessions.reduce<Record<string, RecordedSession[]>>((acc, s) => {
    const v = s.appVersion.version || 'vDesconhecida';
    if (!acc[v]) acc[v] = [];
    acc[v].push(s);
    return acc;
  }, {});

  const sortedVersions = Object.keys(versionGroups).sort((a, b) => {
    if (a === currentVersion) return -1;
    if (b === currentVersion) return 1;
    return b.localeCompare(a);
  });

  return (
    <div className="ac-tester-panel">
      {/* BARRA DE CONTROLE SUPERIOR */}
      <div className="ac-tester-panel-header">
        <div className="ac-tester-panel-title-row">
          <div className="ac-tester-panel-title">
            <Activity size={18} className="ac-pulse-icon" />
            <span>Auditoria de Sessões & Replay</span>
            <span className="ac-tester-count-badge">{sessions.length} gravadas</span>
          </div>

          <div className="ac-tester-panel-actions">
            {/* MASTER SWITCH LIGAR / DESLIGAR */}
            <button 
              onClick={handleToggleRecorder} 
              className={`ac-tester-toggle-btn ${recorderEnabled ? 'enabled' : 'disabled'}`}
              title="Ligar/Desligar Gravação Automática de Sessão"
            >
              {recorderEnabled ? (
                <>
                  <ToggleRight size={18} color="#10b981" />
                  <span>Gravação: <strong>ATIVA</strong></span>
                </>
              ) : (
                <>
                  <ToggleLeft size={18} color="#94a3b8" />
                  <span>Gravação: <strong>PAUSADA</strong></span>
                </>
              )}
            </button>

            <button onClick={reloadSessions} className="ac-tester-btn-icon" title="Recarregar">
              <RefreshCw size={14} />
            </button>

            {sessions.length > 0 && (
              <button onClick={handleClearAll} className="ac-tester-btn-danger" title="Limpar Tudo">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>

        {/* FILTROS E BUSCA */}
        <div className="ac-tester-filters-row">
          <div className="ac-tester-search-box">
            <Search size={14} />
            <input
              type="text"
              placeholder="Buscar por usuário, email, cidade ou rota..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="ac-tester-search-input"
            />
          </div>

          <div className="ac-tester-version-filter-tabs">
            <button
              onClick={() => setVersionFilter('all')}
              className={`ac-tester-tab-btn ${versionFilter === 'all' ? 'active' : ''}`}
            >
              Todas ({sessions.length})
            </button>
            <button
              onClick={() => setVersionFilter('current')}
              className={`ac-tester-tab-btn ${versionFilter === 'current' ? 'active' : ''}`}
            >
              Versão Atual ({sessions.filter((s) => s.appVersion.version === currentVersion).length})
            </button>
            <button
              onClick={() => setVersionFilter('outdated')}
              className={`ac-tester-tab-btn ${versionFilter === 'outdated' ? 'active' : ''}`}
            >
              Versões Antigas ({sessions.filter((s) => s.appVersion.version !== currentVersion).length})
            </button>
          </div>
        </div>
      </div>

      {/* LISTA DE SESSÕES AGRUPADAS */}
      <div className="ac-tester-sessions-list">
        {sortedVersions.length === 0 ? (
          <div className="ac-tester-empty-state">
            <Layers size={36} opacity={0.3} />
            <p>Nenhuma sessão gravada correspondente aos filtros.</p>
            <span>Interaja com a aplicação ou navegue entre páginas para registrar novas sessões.</span>
          </div>
        ) : (
          sortedVersions.map((versionKey) => {
            const groupSessions = versionGroups[versionKey];
            const isCurrent = versionKey === currentVersion;

            return (
              <div key={versionKey} className="ac-tester-version-group">
                <div className={`ac-tester-group-header ${isCurrent ? 'current' : 'outdated'}`}>
                  <div className="ac-tester-group-header-title">
                    {isCurrent ? <ShieldCheck size={16} color="#10b981" /> : <AlertTriangle size={16} color="#f59e0b" />}
                    <strong>Build {versionKey}</strong>
                    {isCurrent && <span className="ac-tester-current-tag">Sincronizada</span>}
                    {!isCurrent && <span className="ac-tester-outdated-tag">Versão Anterior</span>}
                  </div>
                  <span className="ac-tester-group-count">{groupSessions.length} sessões</span>
                </div>

                <div className="ac-tester-cards-grid">
                  {groupSessions.map((session) => {
                    const started = new Date(session.startedAt);
                    const formattedDate = started.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
                    const formattedTime = started.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                    const mapUrl = session.location
                      ? `https://www.google.com/maps?q=${session.location.latitude},${session.location.longitude}`
                      : null;

                    return (
                      <div 
                        key={session.id} 
                        className="ac-tester-session-card"
                        onClick={() => setSelectedSessionForReplay(session)}
                      >
                        {/* CABEÇALHO DO CARD */}
                        <div className="ac-tester-card-top">
                          <div className="ac-tester-card-user">
                            <div className="ac-tester-card-avatar">
                              {session.user.nome.charAt(0).toUpperCase()}
                            </div>
                            <div className="ac-tester-card-user-info">
                              <strong className="ac-tester-user-title">{session.user.nome}</strong>
                              <span className="ac-tester-user-sub">{session.user.email}</span>
                            </div>
                          </div>

                          <div className="ac-tester-card-time">
                            <Clock size={12} />
                            <span>{formattedDate} às {formattedTime}</span>
                          </div>
                        </div>

                        {/* BADGES DE LOCALIZAÇÃO E DISPOSITIVO */}
                        <div className="ac-tester-card-badges">
                          {session.location ? (
                            <a 
                              href={mapUrl!} 
                              target="_blank" 
                              rel="noreferrer" 
                              className="ac-tester-badge-location"
                              onClick={(e) => e.stopPropagation()}
                              title="Ver coordenadas no Google Maps"
                            >
                              <MapPin size={11} />
                              <span>{session.location.city ? `${session.location.city}` : `${session.location.latitude.toFixed(2)}, ${session.location.longitude.toFixed(2)}`}</span>
                              <ExternalLink size={9} />
                            </a>
                          ) : (
                            <span className="ac-tester-badge-neutral">
                              <MapPin size={11} /> Sem GPS
                            </span>
                          )}

                          <span className="ac-tester-badge-device">
                            {session.device.deviceType === 'mobile' ? <Smartphone size={11} /> : <Monitor size={11} />}
                            {session.device.browser} ({session.device.os})
                          </span>

                          <span className="ac-tester-badge-role">
                            {session.user.perfil}
                          </span>
                        </div>

                        {/* ROTAS NAVEGADAS */}
                        <div className="ac-tester-card-routes">
                          <div className="ac-tester-routes-label">Telas Navegadas:</div>
                          <div className="ac-tester-routes-tags">
                            {session.visitedRoutes.slice(0, 3).map((r, i) => (
                              <span key={i} className="ac-tester-route-pill">{r}</span>
                            ))}
                            {session.visitedRoutes.length > 3 && (
                              <span className="ac-tester-route-pill more">+{session.visitedRoutes.length - 3}</span>
                            )}
                          </div>
                        </div>

                        {/* MÉTRICAS & BOTÕES DE AÇÃO */}
                        <div className="ac-tester-card-footer">
                          <div className="ac-tester-card-metrics">
                            <span>👆 <strong>{session.totalClicks}</strong> cliques</span>
                            <span>⏱️ <strong>{session.activeSeconds}s</strong> ativos</span>
                            {session.totalErrors > 0 && (
                              <span style={{ color: '#ef4444' }}>❌ {session.totalErrors} erros</span>
                            )}
                          </div>

                          <div className="ac-tester-card-btns">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                exportSessionAsJson(session);
                              }}
                              className="ac-tester-btn-icon-subtle"
                              title="Baixar JSON"
                            >
                              <Download size={13} />
                            </button>

                            <button
                              onClick={(e) => handleDeleteSession(session.id, e)}
                              className="ac-tester-btn-icon-subtle danger"
                              title="Remover"
                            >
                              <Trash2 size={13} />
                            </button>

                            <button 
                              className="ac-tester-btn-play-action"
                              title="Assistir Replay Visual"
                            >
                              <Play size={13} />
                              Replay
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* MODAL DE REPRODUÇÃO VISUAL SE ABERTO */}
      {selectedSessionForReplay && (
        <SessionReplayerModal
          session={selectedSessionForReplay}
          onClose={() => setSelectedSessionForReplay(null)}
        />
      )}
    </div>
  );
};
